import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import loadGoogleScript from '../utils/loadGoogleScript';

// Renders Google's own Identity Services button. onCredential receives the raw Google
// ID token — verification happens server-side (POST /api/auth/google), never here.
// CLIENT_ID is read inside the component (not a module-level constant) so tests can vary
// it per-case via process.env without needing to reset/re-require the module (which would
// otherwise create a second React instance and break hooks) — CRA's webpack DefinePlugin
// inlines process.env.REACT_APP_* at build time regardless of where the expression sits.
function GoogleSignInButton({ onCredential, disabled }) {
  const { t } = useTranslation('login');
  const containerRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);
  const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!CLIENT_ID) {
      // Expected in local dev until the owner creates a Google OAuth client and sets
      // REACT_APP_GOOGLE_CLIENT_ID — fail soft, keep the password form usable.
      console.warn('REACT_APP_GOOGLE_CLIENT_ID is not set — Google sign-in button disabled.');
      setUnavailable(true);
      return;
    }

    let cancelled = false;

    loadGoogleScript()
      .then((google) => {
        if (cancelled || !containerRef.current) return;
        google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (response) => onCredential(response.credential),
        });
        google.accounts.id.renderButton(containerRef.current, {
          theme: 'outline',
          size: 'large',
          width: 360,
          text: 'continue_with',
        });
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setUnavailable(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (unavailable) return null;

  return (
    <div
      ref={containerRef}
      aria-label={t('googleSignIn', 'Sign in with Google')}
      className={disabled ? 'pointer-events-none opacity-50' : ''}
    />
  );
}

export default GoogleSignInButton;
